import React from 'react';

import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  CODE_MIME_TYPES,
  CSV_MIME_TYPES,
  getSupportedPreviewMimeType,
  IMAGE_MIME_TYPES,
  MAX_FILE_SIZE_FOR_PREVIEW_BYTES,
  MOLSTAR_MIME_TYPE_TO_FORMAT,
  PDF_MIME_TYPE,
  TEXT_MIME_TYPES,
} from './FilePreviewConstants';
import CodeRenderer from './renderers/CodeRenderer';
import CSVRenderer from './renderers/CSVRenderer';
import IFrameRenderer from './renderers/IFrameRenderer';
import ImageRenderer from './renderers/ImageRenderer';
import MarkdownRenderer from './renderers/MarkdownRenderer';
import MolstarRenderer from './renderers/MolstarRenderer';
import PdfRenderer from './renderers/PdfRenderer';
import TextRenderer from './renderers/TextRenderer';
import {useTextContentFromUrl} from './useTextContentFromUrl';

export interface FilePreviewProps {
  url: string;
  mimeType: string;
  fileSize?: number | null;
  filename?: string;
  fallback?: React.ReactNode;
  htmlOptions?: {
    /**
     * @dangerous Enables script execution inside the iframe sandbox. Only use for trusted
     * content (e.g. JMP HTML reports) where scripts are required for rendering.
     */
    dangerouslyAllowScripts?: boolean;
  };
}

export default function FilePreview({
  url,
  mimeType,
  fileSize,
  filename,
  fallback,
  htmlOptions,
}: FilePreviewProps): JSX.Element {
  if (fileSize != null && fileSize > MAX_FILE_SIZE_FOR_PREVIEW_BYTES) {
    return (
      <InlineBanner
        bannerType={InlineBannerType.WARNING}
        text="This file is too large to preview."
        supportingInfo="Download the file to view its contents."
      />
    );
  }

  const effectiveMimeType = getSupportedPreviewMimeType(mimeType, filename) ?? '';

  if (needsTextContent(effectiveMimeType)) {
    return <TextContentPreview url={url} mimeType={effectiveMimeType} fallback={fallback} />;
  }
  return renderUrlPreview(url, effectiveMimeType, {filename, htmlOptions, fallback});
}

function needsTextContent(mimeType: string): boolean {
  return (
    TEXT_MIME_TYPES.has(mimeType) ||
    CSV_MIME_TYPES.has(mimeType) ||
    mimeType === 'text/markdown' ||
    CODE_MIME_TYPES[mimeType] != null
  );
}

function TextContentPreview({
  url,
  mimeType,
  fallback,
}: {
  url: string;
  mimeType: string;
  fallback?: React.ReactNode;
}): JSX.Element {
  const {content, isLoading, error} = useTextContentFromUrl(url);

  if (isLoading) {
    return <CenteredSpinner />;
  }
  if (error) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />;
  }

  const text = content ?? '';

  if (TEXT_MIME_TYPES.has(mimeType)) {
    return <TextRenderer content={text} />;
  }
  if (mimeType === 'text/markdown') {
    return <MarkdownRenderer content={text} />;
  }
  if (CSV_MIME_TYPES.has(mimeType)) {
    return <CSVRenderer content={text} delimiter={mimeType === 'text/tsv' ? '\t' : ','} />;
  }
  if (CODE_MIME_TYPES[mimeType]) {
    return <CodeRenderer content={text} language={CODE_MIME_TYPES[mimeType]} />;
  }

  if (fallback) {
    return <>{fallback}</>;
  }
  return <InlineBanner bannerType={InlineBannerType.INFO} text="Preview not available for this file type." />;
}

function renderUrlPreview(
  url: string,
  mimeType: string,
  opts: {filename?: string; htmlOptions?: FilePreviewProps['htmlOptions']; fallback?: React.ReactNode}
): JSX.Element {
  if (mimeType === 'text/html') {
    return (
      <IFrameRenderer
        url={url}
        title={opts.filename}
        dangerouslyAllowScripts={opts.htmlOptions?.dangerouslyAllowScripts}
      />
    );
  }
  if (IMAGE_MIME_TYPES.has(mimeType)) {
    return <ImageRenderer url={url} alt={opts.filename} />;
  }
  if (mimeType === PDF_MIME_TYPE) {
    return <PdfRenderer url={url} title={opts.filename} />;
  }
  if (MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType]) {
    return <MolstarRenderer url={url} format={MOLSTAR_MIME_TYPE_TO_FORMAT[mimeType]} />;
  }

  if (opts.fallback) {
    return <>{opts.fallback}</>;
  }
  return <InlineBanner bannerType={InlineBannerType.INFO} text="Preview not available for this file type." />;
}
